import { AccountingDashboardSnapshot } from '@/renderer/types/accounting';
import { AlertTriangle, CheckCircle2, Clock3, Settings2 } from 'lucide-react';

interface AccountingActionCenterProps {
  snapshot: AccountingDashboardSnapshot;
}

export function AccountingActionCenter({
  snapshot,
}: AccountingActionCenterProps) {
  const setupItems = [
    snapshot.openPeriods === 0
      ? 'Open an accounting period before posting additional journals.'
      : null,
    snapshot.unmappedPaymentMethods > 0
      ? `${snapshot.unmappedPaymentMethods.toLocaleString()} payment method(s) are still not mapped to ledger accounts.`
      : null,
    snapshot.configuredPostingRules === 0
      ? 'Configure posting rules so operational events journal automatically.'
      : null,
    snapshot.activeTaxRates === 0
      ? 'Set up active tax rates before relying on tax automation.'
      : null,
  ].filter(Boolean) as string[];

  const operationalItems = [
    snapshot.draftJournals > 0
      ? `${snapshot.draftJournals.toLocaleString()} draft journal(s) still need review or posting.`
      : null,
    snapshot.upcomingCheckouts > 0
      ? `${snapshot.upcomingCheckouts.toLocaleString()} booking(s) are due to check out within the next 3 days.`
      : null,
    snapshot.unpaidBookings > 0
      ? `${snapshot.unpaidBookings.toLocaleString()} booking(s) still have unpaid balance.`
      : null,
    snapshot.overdueReceivables > 0
      ? `${snapshot.overdueReceivables.toLocaleString()} booking receivable(s) are already overdue based on checkout date.`
      : null,
    snapshot.totalSettlements === 0
      ? 'No settlements landed in the selected period. Verify outlet activity or date filters.'
      : null,
    snapshot.totalTransactions === 0
      ? 'No posted accounting transactions were recognized in the selected period.'
      : null,
  ].filter(Boolean) as string[];

  const needsAttention = [...setupItems, ...operationalItems];

  return (
    <div className="self-start rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-950">
            {needsAttention.length ? 'Action required' : 'Tasks for today'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {needsAttention.length
              ? 'Only the blockers that affect accounting confidence and daily operations.'
              : 'Your setup is healthy. Here are the operational highlights for today.'}
          </p>
        </div>
        <div
          className={[
            'flex h-11 w-11 items-center justify-center rounded-2xl',
            needsAttention.length
              ? 'bg-pos-primary/10 text-pos-primary'
              : 'bg-emerald-50 text-emerald-600',
          ].join(' ')}
        >
          {needsAttention.length ? (
            <AlertTriangle size={20} />
          ) : (
            <CheckCircle2 size={20} />
          )}
        </div>
      </div>

      <div className="mt-4 max-h-[280px] space-y-2.5 overflow-y-auto">
        {needsAttention.length ? (
          needsAttention.map((item) => (
            <div
              key={item}
              className="flex items-start gap-3 rounded-[22px] border border-pos-primary/15 bg-pos-primary/8 px-4 py-3"
            >
              <AlertTriangle size={16} className="mt-0.5 text-pos-primary" />
              <p className="text-sm text-slate-700">{item}</p>
            </div>
          ))
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
              <Clock3 size={16} className="mt-0.5 text-pos-primary" />
              <p className="text-sm text-slate-700">
                {snapshot.totalSettlements.toLocaleString()} settlement event(s)
                and {snapshot.totalTransactions.toLocaleString()} posted journal
                transaction(s) were recognized in this period.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
              <Settings2 size={16} className="mt-0.5 text-pos-primary" />
              <p className="text-sm text-slate-700">
                Payment mappings, posting rules, periods, and tax setup are all
                in place for this dashboard scope.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
